import { Box, Checkbox, Stack, Theme, Typography, useTheme } from "@mui/material";
import type { JSX, ReactNode } from "react";

import { colors } from "@/theme/tokens/colors";

const createStyles = (
  theme: Theme,
  selectable: boolean,
  disabled?: boolean,
  isSelected?: boolean,
  compactPriceLayout?: boolean,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
) => ({
  mainContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: isSelected || !selectable ? colors.brand.blueLight : "#FFFFFF",
    border:
      isSelected || !selectable ? `1px solid ${colors.brand.blue}` : `1px solid ${theme.semanticColors.border.subtle}`,
    borderRadius: "5px",
    padding: "10px",
    width: "100%",
    cursor: disabled || !selectable ? "default" : "pointer",
  },
  rowFlex: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
  },
  mainRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: compactPriceLayout ? "flex-start" : "space-between",
    gap: "12px",
    width: "100%",
  },
  mainText: {
    fontWeight: 600,
  },
  titleGroup: {
    flex: compactPriceLayout ? "0 1 auto" : "1 1 auto",
    minWidth: 0,
  },
  priceText: {
    fontWeight: 600,
    flex: compactPriceLayout ? "1 1 0" : "0 1 auto",
    minWidth: 0,
    textAlign: "right",
  },
  checkbox: {
    p: 0,
    "input:hover ~ &": {
      backgroundColor: "transparent",
    },
  },
});

interface ValuationPriceTypeBoxProps {
  title: string;
  subTitle: string;
  priceMain: ReactNode;
  priceSub?: string | null;
  selectable: boolean;
  disabled?: boolean;
  isSelected?: boolean;
  setIsSelected?: (isSelected: boolean) => void;
  compactPriceLayout?: boolean;
}

export const ValuationPriceTypeBox = ({
  title,
  subTitle,
  priceMain,
  priceSub,
  selectable,
  disabled,
  isSelected,
  setIsSelected,
  compactPriceLayout = false,
}: ValuationPriceTypeBoxProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme, selectable, disabled, isSelected, compactPriceLayout);

  const handleChange = (checked: boolean): void => {
    if (setIsSelected && !disabled) {
      setIsSelected(checked);
    }
  };

  const renderPriceMain = (): ReactNode => {
    if (typeof priceMain !== "string") {
      return priceMain;
    }

    const separator = " - ";
    const separatorIndex = priceMain.indexOf(separator);

    if (separatorIndex === -1) {
      return priceMain;
    }

    const start = priceMain.slice(0, separatorIndex);
    const end = priceMain.slice(separatorIndex + separator.length);

    return (
      <>
        {start}
        {separator}
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          {end}
        </Box>
      </>
    );
  };

  return (
    <Box sx={styles.mainContainer} onClick={() => handleChange(!isSelected)}>
      <Stack alignItems={"stretch"} direction="column" spacing={theme.analysisLayout.cardGap} sx={{ width: "100%" }}>
        <Box sx={styles.mainRow}>
          <Stack direction="row" spacing="12px" alignItems={"center"} sx={styles.titleGroup}>
            {selectable && (
              <Checkbox
                checked={isSelected}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                  event.stopPropagation();
                  handleChange(event.target.checked);
                }}
                disabled={disabled}
                sx={styles.checkbox}
                disableRipple
              />
            )}
            <Typography sx={styles.mainText}>{title}</Typography>
          </Stack>
          {typeof priceMain === "string" ? (
            <Typography sx={styles.priceText}>{renderPriceMain()}</Typography>
          ) : (
            <Box sx={styles.priceText}>{priceMain}</Box>
          )}
        </Box>
        <Box sx={styles.rowFlex}>
          <Typography>{subTitle}</Typography>
          {priceSub && <Typography>{priceSub}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
};
